import { departmentMatches } from './quotaDirectory';
import type {
  GatewayApiKey,
  QuotaAssignment,
  QuotaDirectory,
  QuotaKind,
  QuotaResourceType,
  QuotaScope,
  QuotaSource,
  QuotaUsage,
  QuotaUser,
  QuotaUserStatus,
} from './governedMcpTypes';

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export function resourceTypeFromQuotaKind(kind: QuotaKind): QuotaResourceType {
  if (kind === 'tool') return 'mcp_tool';
  if (kind === 'llm') return 'llm_gateway';
  return 'client';
}

export function inferQuotaResourceType(
  item: Pick<QuotaAssignment, 'clientId'> & { resourceType?: string; resourceId?: string },
  clientIds?: Set<string>,
  gatewayIds?: Set<string>
): QuotaResourceType {
  if (item.resourceType === 'client' || item.resourceType === 'mcp_tool' || item.resourceType === 'llm_gateway') {
    return item.resourceType;
  }
  const clientId = (item.clientId || '').trim();
  const resourceId = (item.resourceId || clientId).trim();
  if (gatewayIds?.has(resourceId) || gatewayIds?.has(clientId) || resourceId.startsWith('gw-') || clientId.startsWith('gw-')) {
    return 'llm_gateway';
  }
  if (resourceId && resourceId !== clientId) return 'mcp_tool';
  if (clientIds?.has(clientId) || clientIds?.has(resourceId)) return 'client';
  return 'mcp_tool';
}

export function currentQuotaMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function isQuotaMonth(value: string): boolean {
  return MONTH_RE.test(value.trim());
}

export function formatQuotaMonth(month: string): string {
  if (!isQuotaMonth(month)) return month || '—';
  const [year, monthPart] = month.split('-').map(Number);
  return new Date(year, monthPart - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export function listQuotaFilterMonths(assignments: Pick<QuotaAssignment, 'month'>[]): string[] {
  const months = new Set<string>([currentQuotaMonth()]);
  for (const item of assignments) {
    if (isQuotaMonth(item.month)) months.add(item.month);
  }
  return Array.from(months).sort((left, right) => right.localeCompare(left));
}

export function dailyUsageInMonth<T extends { date: string }>(points: T[] | undefined, month: string): T[] {
  if (!isQuotaMonth(month) || !points?.length) return [];
  return points.filter((point) => point.date.startsWith(`${month}-`) || point.date === month);
}

export function tokensUsedInMonth(usage: Pick<QuotaUsage, 'dailyUsage'> | undefined, month: string): number {
  return dailyUsageInMonth(usage?.dailyUsage, month).reduce((sum, point) => sum + (Number(point.tokens) || 0), 0);
}

export function quotaRecordId(scope: QuotaScope, scopeId: string, clientId: string, resourceId: string, month: string): string {
  const part = (value: string) =>
    value.trim().replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'unknown';
  return `quota-${part(scope)}-${part(scopeId)}-${part(clientId)}-${part(resourceId || clientId)}-${part(month)}`;
}

export type QuotaResourceAlias = { id: string; mcpToolName?: string; publicAlias?: string };

function resourceKeys(item: QuotaResourceAlias): string[] {
  return [item.id, item.mcpToolName, item.publicAlias].map((value) => (value || '').trim()).filter(Boolean);
}

export function sameQuotaResource(left: string, right: string, aliases?: QuotaResourceAlias[]): boolean {
  const a = left.trim();
  const b = right.trim();
  if (!a || !b) return false;
  if (a === b) return true;
  return (aliases || []).some((item) => {
    const keys = resourceKeys(item);
    return keys.includes(a) && keys.includes(b);
  });
}

export function isNewerQuotaAssignment(next: QuotaAssignment, current: QuotaAssignment): boolean {
  const nextTime = Date.parse(next.updatedAt || '') || 0;
  const currentTime = Date.parse(current.updatedAt || '') || 0;
  if (nextTime !== currentTime) return nextTime > currentTime;
  return (next.version || 0) > (current.version || 0);
}

function latestAssignment(items: QuotaAssignment[]): QuotaAssignment | undefined {
  return items.reduce<QuotaAssignment | undefined>((latest, item) => {
    if (!latest || isNewerQuotaAssignment(item, latest)) return item;
    return latest;
  }, undefined);
}

function matchesQuotaIdentity(
  item: QuotaAssignment,
  scope: QuotaScope,
  scopeId: string,
  clientId: string,
  resourceId?: string,
  aliases?: QuotaResourceAlias[]
): boolean {
  if (item.scope !== scope || item.scopeId !== scopeId || item.clientId !== clientId) return false;
  if (!resourceId) return true;
  return sameQuotaResource(item.resourceId || item.clientId, resourceId, aliases);
}

export function findStoredQuotaAssignment(
  assignments: QuotaAssignment[],
  scope: QuotaScope,
  scopeId: string,
  clientId: string,
  month: string,
  resourceId?: string,
  aliases?: QuotaResourceAlias[]
): QuotaAssignment | undefined {
  if (!scopeId || !clientId || !isQuotaMonth(month)) return undefined;
  return latestAssignment(
    assignments.filter(
      (item) => item.month === month && matchesQuotaIdentity(item, scope, scopeId, clientId, resourceId, aliases)
    )
  );
}

export function findQuotaAssignment(
  assignments: QuotaAssignment[],
  scope: QuotaScope,
  scopeId: string,
  clientId: string,
  month: string,
  resourceId?: string,
  aliases?: QuotaResourceAlias[]
): QuotaAssignment | undefined {
  if (!scopeId || !clientId || !isQuotaMonth(month)) return undefined;
  const matches = assignments.filter((item) => matchesQuotaIdentity(item, scope, scopeId, clientId, resourceId, aliases));
  const exact = matches.filter((item) => item.month === month);
  const rolled = matches.filter((item) => item.renewsMonthly && isQuotaMonth(item.month) && item.month < month);
  if (resourceId) {
    return latestAssignment(exact) || latestAssignment(rolled);
  }
  if (exact[0]) return exact[0];
  return rolled.sort((left, right) => right.month.localeCompare(left.month))[0];
}

export function resolveEffectiveQuota(
  assignments: QuotaAssignment[],
  user: { id: string; roleId?: string; departmentId?: string },
  clientId: string,
  month: string,
  directory?: QuotaDirectory
): { tokenLimit: number; source: QuotaSource } {
  const userQuota = findQuotaAssignment(assignments, 'user', user.id, clientId, month);
  if (userQuota?.status === 'active') return { tokenLimit: userQuota.tokenLimit, source: 'user' };
  const roleQuota = user.roleId ? findQuotaAssignment(assignments, 'role', user.roleId, clientId, month) : undefined;
  if (roleQuota?.status === 'active') return { tokenLimit: roleQuota.tokenLimit, source: 'role' };
  if (user.departmentId) {
    const department = directory?.departments.find(
      (item) => departmentMatches(item, user.departmentId || '') || item.id === user.departmentId
    );
    const ids = department ? department.matchIds : [user.departmentId];
    const departmentQuota = assignments.find(
      (item) =>
        item.scope === 'department' &&
        item.clientId === clientId &&
        item.month === month &&
        item.status === 'active' &&
        ids.includes(item.scopeId)
    );
    if (departmentQuota) return { tokenLimit: departmentQuota.tokenLimit, source: 'department' };
  }
  return { tokenLimit: 0, source: 'none' };
}

export function quotaUserFromAssignment(
  assignment: QuotaAssignment,
  directory: QuotaDirectory,
  usages: QuotaUsage[],
  month: string
): QuotaUser {
  const userId = assignment.scopeId.trim();
  const clientId = assignment.clientId.trim();
  const dirUser = directory.users.find((item) => item.id === userId);
  const usage = usages.find((item) => item.userId === userId && item.clientId === clientId);
  return {
    id: usage?.id || assignment.id || `usage-${userId}-${clientId}`,
    clientId,
    userId,
    name: assignment.name || dirUser?.name || userId,
    email: dirUser?.email || '',
    role: dirUser?.roleName || '',
    roleId: dirUser?.roleId || '',
    departmentId: dirUser?.departmentId || '',
    source: 'user',
    tokenLimit: assignment.status === 'active' ? assignment.tokenLimit : 0,
    tokensUsed: tokensUsedInMonth(usage, month),
    dailyUsage: dailyUsageInMonth(usage?.dailyUsage, month),
    byModel: usage?.byModel ?? [],
    updatedAt: usage?.updatedAt || assignment.updatedAt,
    month: isQuotaMonth(month) ? month : assignment.month,
  };
}

export function buildAssignedQuotaUsers(
  assignments: QuotaAssignment[],
  usages: QuotaUsage[],
  directory: QuotaDirectory,
  month: string,
  clientIds?: Set<string>,
  resourceType: QuotaResourceType = 'client',
  gatewayIds?: Set<string>
): QuotaUser[] {
  const allMonths = month === 'all';
  const seen = new Set<string>();
  const rows: QuotaUser[] = [];
  for (const assignment of assignments) {
    if (assignment.scope !== 'user') continue;
    const userId = assignment.scopeId.trim();
    const clientId = assignment.clientId.trim();
    if (!userId || !clientId) continue;
    if (clientIds && !clientIds.has(clientId)) continue;
    if (inferQuotaResourceType(assignment, clientIds, gatewayIds) !== resourceType) continue;
    const rowMonth = allMonths ? assignment.month : month;
    const resourceId = resourceType === 'client' ? clientId : assignment.resourceId || clientId;
    const applied = allMonths
      ? findStoredQuotaAssignment(assignments, 'user', userId, clientId, rowMonth, resourceId)
      : findQuotaAssignment(assignments, 'user', userId, clientId, rowMonth, resourceId);
    if (!applied || applied.id !== assignment.id) continue;
    const pair = allMonths ? `${userId}::${clientId}::${rowMonth}` : `${userId}::${clientId}`;
    if (seen.has(pair)) continue;
    seen.add(pair);
    const row = quotaUserFromAssignment(applied, directory, usages, rowMonth);
    rows.push(allMonths ? { ...row, id: `${row.id}-${rowMonth}` } : row);
  }
  return rows;
}

export function buildEffectiveQuotaUsers(
  apiKeys: GatewayApiKey[],
  assignments: QuotaAssignment[],
  usages: QuotaUsage[],
  directory: QuotaDirectory,
  month: string
): QuotaUser[] {
  const seen = new Set<string>();
  const rows: QuotaUser[] = [];
  for (const key of apiKeys) {
    const userId = key.userId.trim();
    const clientId = key.clientId.trim();
    if (!userId || !clientId) continue;
    const pair = `${userId}::${clientId}`;
    if (seen.has(pair)) continue;
    seen.add(pair);
    const dirUser = directory.users.find((item) => item.id === userId);
    const resolved = resolveEffectiveQuota(
      assignments,
      { id: userId, roleId: dirUser?.roleId, departmentId: dirUser?.departmentId },
      clientId,
      month,
      directory
    );
    const usage = usages.find((item) => item.userId === userId && item.clientId === clientId);
    const dailyUsage = dailyUsageInMonth(usage?.dailyUsage, month);
    rows.push({
      id: usage?.id || `usage-${userId}-${clientId}`,
      clientId,
      userId,
      name: dirUser?.name || key.name || key.username || userId,
      email: dirUser?.email || key.email || '',
      role: dirUser?.roleName || '',
      roleId: dirUser?.roleId || '',
      departmentId: dirUser?.departmentId || '',
      source: resolved.source,
      tokenLimit: resolved.tokenLimit,
      tokensUsed: tokensUsedInMonth(usage, month),
      dailyUsage,
      byModel: usage?.byModel ?? [],
      updatedAt: usage?.updatedAt || key.updatedAt,
    });
  }
  for (const assignment of assignments) {
    if (assignment.scope !== 'user') continue;
    const userId = assignment.scopeId.trim();
    const clientId = assignment.clientId.trim();
    if (!userId || !clientId) continue;
    const pair = `${userId}::${clientId}`;
    if (seen.has(pair)) continue;
    const applied = findQuotaAssignment(assignments, 'user', userId, clientId, month);
    if (!applied || applied.tokenLimit <= 0) continue;
    seen.add(pair);
    const dirUser = directory.users.find((item) => item.id === userId);
    const usage = usages.find((item) => item.userId === userId && item.clientId === clientId);
    rows.push({
      id: usage?.id || applied.id || `usage-${userId}-${clientId}`,
      clientId,
      userId,
      name: applied.name || dirUser?.name || userId,
      email: dirUser?.email || '',
      role: dirUser?.roleName || '',
      roleId: dirUser?.roleId || '',
      departmentId: dirUser?.departmentId || '',
      source: 'user',
      tokenLimit: applied.status === 'active' ? applied.tokenLimit : 0,
      tokensUsed: tokensUsedInMonth(usage, month),
      dailyUsage: dailyUsageInMonth(usage?.dailyUsage, month),
      byModel: usage?.byModel ?? [],
      updatedAt: usage?.updatedAt || applied.updatedAt,
    });
  }
  return rows;
}

export function sourceLabel(source: QuotaSource | undefined): string {
  if (source === 'user') return 'User';
  if (source === 'role') return 'Role';
  if (source === 'department') return 'Department';
  return 'None';
}

export function departmentMemberIds(directory: QuotaDirectory, departmentId: string): string[] {
  const department = directory.departments.find((item) => departmentMatches(item, departmentId) || item.id === departmentId);
  if (!department) return directory.users.filter((user) => user.departmentId === departmentId).map((user) => user.id);
  return directory.users
    .filter((user) => department.matchIds.includes(user.departmentId) || user.departmentId === department.id)
    .map((user) => user.id);
}

export function remainingTokens(user: Pick<QuotaUser, 'tokenLimit' | 'tokensUsed'>): number {
  return Math.max(0, user.tokenLimit - user.tokensUsed);
}

export function quotaUtilization(user: Pick<QuotaUser, 'tokenLimit' | 'tokensUsed'>): number {
  if (user.tokenLimit <= 0) return user.tokensUsed > 0 ? 1 : 0;
  return user.tokensUsed / user.tokenLimit;
}

export function quotaStatus(user: Pick<QuotaUser, 'tokenLimit' | 'tokensUsed'>): QuotaUserStatus {
  const used = quotaUtilization(user);
  if (used >= 1) return 'exceeded';
  if (used >= 0.8) return 'warning';
  return 'ok';
}

export function formatTokens(value: number): string {
  return new Intl.NumberFormat('en-US').format(Math.max(0, Math.round(value)));
}

export function utilizationPercent(user: Pick<QuotaUser, 'tokenLimit' | 'tokensUsed'>): number {
  return Math.min(100, Math.round(quotaUtilization(user) * 100));
}

export function compactTokens(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return formatTokens(value);
}

export function aggregateDailySpend(users: QuotaUser[]): { date: string; tokens: number }[] {
  const byDate = new Map<string, number>();
  for (const user of users) {
    for (const point of user.dailyUsage) {
      byDate.set(point.date, (byDate.get(point.date) ?? 0) + point.tokens);
    }
  }
  return Array.from(byDate.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([date, tokens]) => ({ date, tokens }));
}

export function aggregateModelSpend(users: QuotaUser[]): { model: string; tokens: number }[] {
  const byModel = new Map<string, number>();
  for (const user of users) {
    for (const row of user.byModel) {
      byModel.set(row.model, (byModel.get(row.model) ?? 0) + row.tokens);
    }
  }
  return Array.from(byModel.entries())
    .map(([model, tokens]) => ({ model, tokens }))
    .sort((left, right) => right.tokens - left.tokens);
}

export function utilizationBreakdown(users: QuotaUser[]): { name: string; key: QuotaUserStatus; value: number }[] {
  const counts: Record<QuotaUserStatus, number> = { ok: 0, warning: 0, exceeded: 0 };
  for (const user of users) counts[quotaStatus(user)] += 1;
  return [
    { name: 'On track', key: 'ok', value: counts.ok },
    { name: 'High', key: 'warning', value: counts.warning },
    { name: 'Over limit', key: 'exceeded', value: counts.exceeded },
  ];
}

export function filterQuotaUsers(users: QuotaUser[], clientId: string): QuotaUser[] {
  if (!clientId || clientId === 'all') return users;
  return users.filter((user) => user.clientId === clientId);
}

export function clientLabel(clientId: string, clients: { id: string; name: string }[]): string {
  return clients.find((item) => item.id === clientId)?.name || 'Unassigned client';
}

export function aggregateClientSpend(
  users: QuotaUser[],
  clients: { id: string; name: string }[]
): { clientId: string; name: string; tokens: number }[] {
  const byClient = new Map<string, number>();
  for (const user of users) {
    byClient.set(user.clientId, (byClient.get(user.clientId) ?? 0) + user.tokensUsed);
  }
  return Array.from(byClient.entries())
    .map(([clientId, tokens]) => ({
      clientId,
      name: clientLabel(clientId, clients),
      tokens,
    }))
    .sort((left, right) => right.tokens - left.tokens);
}
